import { IoIosSend } from "react-icons/io";
import styles from "./styles.module.scss";
import React from "react";
import { useChatLog } from "../../../contexts/chat-log";
import { sendMessage, initializeChat, createAiMessageId } from "../../../libs/fns";

export default function Question({ onUserMessageAdded }: { onUserMessageAdded?: () => void }) {
  const [question, setQuestion] = React.useState<string>("");
  // 前のAI応答がストリーミング中に送信された次のメッセージ（完了後に自動送信するためのキュー、最新の1件のみ保持）
  const pendingMessageRef = React.useRef<string | null>(null);

  const { chatLogs, setChatLogs, resetIdleTimer } = useChatLog();

  const isStreamingActive = chatLogs.some(
    (chatLog) =>
      chatLog.type === "LOADING" ||
      (chatLog.type === "AI" && chatLog.message.streaming_completed === false)
  );

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = event.target.value;
    setQuestion(value);
    event.target.style.height = 'auto';
    event.target.style.height = Math.min(event.target.scrollHeight, 120) + 'px';
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleClick();
    }
  }

  function dequeuePendingMessage() {
    const next = pendingMessageRef.current;
    if (!next) return;
    pendingMessageRef.current = null;
    void submitMessage(next);
  }

  function handleClick() {
    if (question === "") return;

    const tmp = question;
    setQuestion("");

    if (isStreamingActive) {
      // 前の応答がストリーミング中: 完了後に自動送信するためキューに積む（最新の1件のみ保持）
      pendingMessageRef.current = tmp;
      return;
    }

    void submitMessage(tmp);
  }

  async function submitMessage(tmp: string) {
    resetIdleTimer();

    // 送信ボタンクリック時に全てのAIメッセージの提問ボタンを非表示にする
    setChatLogs((prev) =>
      prev.map((log) => {
        if (log.type === "AI") {
          return {
            ...log,
            message: {
              ...log.message,
              answers_used: true,
            },
          };
        }
        return log;
      })
    );

    // ユーザーメッセージを追加
    setChatLogs((prev) => [
      ...prev,
      {
        type: "USER",
        message: {
          model: tmp,
          answer: [],
          url: [],
          asset_data: [],
        },
      },
    ]);

    // ユーザーメッセージ追加直後にスクロール
    if (onUserMessageAdded) {
      onUserMessageAdded();
    }

    // ストリーミング中のAIメッセージを追加（初期状態はstreaming_completed: false）
    // このクリックで生成するAIメッセージの一意ID。配列上の位置（インデックス）
    // ではなくIDで対象を特定することで、session_reset_required等で
    // chatLogsが丸ごと入れ替わったり、別の送信(Chat側)が並行して
    // ストリーミングしていても、他のメッセージの内容を誤って
    // 上書き・削除しないようにする
    const thisAiMessageId = createAiMessageId();
    setChatLogs((prev) => [
      ...prev,
      {
        type: "AI" as const,
        id: thisAiMessageId,
        message: {
          model: "",
          answer: [],
          url: [],
          asset_data: [],
          streaming_completed: false,
        },
      },
    ]);

    try {
      // APIを呼び出し（ストリーミング対応）
      const message = await sendMessage(
        tmp,
        (accumulatedText: string, _newChunk: string) => {
          // ストリーミング中のテキスト更新（全体テキストを使用）
          setChatLogs((prev) => {
            const targetIndex = prev.findIndex((log) => log.id === thisAiMessageId);
            if (targetIndex === -1) return prev;
            return prev.map((log, index) => {
              if (index === targetIndex) {
                return {
                  ...log,
                  message: {
                    ...log.message,
                    model: accumulatedText, // 累積テキスト全体を保存
                    streaming_completed: false,
                  },
                };
              }
              return log;
            });
          });
        },
        (meta) => {
          // メタデータが解析され次第、本文と同時進行でAIメッセージへ即時反映する
          setChatLogs((prev) => {
            const targetIndex = prev.findIndex((log) => log.id === thisAiMessageId);
            if (targetIndex === -1) return prev;
            return prev.map((log, index) => {
              if (index === targetIndex) {
                return {
                  ...log,
                  message: {
                    ...log.message,
                    ai_question: meta.ai_question || log.message.ai_question,
                    answer: meta.answer.length ? meta.answer : log.message.answer,
                    campaign_text: meta.campaign_text || log.message.campaign_text,
                    cta_data: meta.cta_data.length ? meta.cta_data : log.message.cta_data,
                    display_link: meta.display_link.length ? meta.display_link : log.message.display_link,
                    asset_data: meta.asset_data.length ? meta.asset_data : log.message.asset_data,
                    asset_description: meta.asset_description || log.message.asset_description,
                  },
                };
              }
              return log;
            });
          });
        },
        async (resetMessage?: string) => {
          // onReset: セッション初期化時にチャットログをクリア
          // リセットメッセージがある場合は最上段に残す
          console.log('チャットログをクリアします');
          if (resetMessage) {
            // まずリセットメッセージのみ表示
            setChatLogs([
              {
                type: "AI",
                message: {
                  model: resetMessage,
                  answer: [],
                  url: [],
                  asset_data: [],
                  streaming_completed: true,
                },
              },
            ]);

            // initializeChatを呼び出してバナーCTA（リンク画像型）を取得
            try {
              const { fullResponse } = await initializeChat(window.location.href);
              if (fullResponse) {
                // リンク+画像のCTAをフィルタリング（type !== 1 かつ image あり）
                const isLinkImageCta = (cta: any) =>
                  cta.image && cta.type !== 1 && cta.type !== "1";
                const screen2LinkCtas = (fullResponse.cta_data?.cta_data ?? []).filter(isLinkImageCta);
                const bottomLinkCtas = ((fullResponse.bottom_view_data as any)?.data ?? []).filter(isLinkImageCta);
                const bannerCtaData = [...screen2LinkCtas, ...bottomLinkCtas];

                if (bannerCtaData.length > 0) {
                  setChatLogs([
                    {
                      type: "AI",
                      message: {
                        model: resetMessage,
                        answer: [],
                        url: [],
                        asset_data: [],
                        banner_cta_data: bannerCtaData,
                        streaming_completed: true,
                      },
                    },
                  ]);
                }
              }
            } catch (error) {
              console.error('[Reset] 再初期化中にエラーが発生しました:', error);
            }
          } else {
            setChatLogs([]);
          }
        }
      );

      // ストリーミング完了時：段階的に表示
      // 1. まずテキスト完了状態にする（answersを表示）
      setChatLogs((prev) => {
        const targetIndex = prev.findIndex((log) => log.id === thisAiMessageId);
        if (targetIndex === -1) return prev;
        return prev.map((log, index) => {
          if (index === targetIndex) {
            return {
              ...log,
              message: {
                ...message,
                streaming_completed: 'text_completed', // テキスト完了状態
              },
            };
          }
          return log;
        });
      });

      // 2. 短いディレイ後、完全完了状態にする（asset_dataを表示）
      setTimeout(() => {
        setChatLogs((prev) => {
          const targetIndex = prev.findIndex((log) => log.id === thisAiMessageId);
          if (targetIndex === -1) return prev;
          return prev.map((log, index) => {
            if (index === targetIndex) {
              return {
                ...log,
                message: {
                  ...message,
                  streaming_completed: true, // 完全完了
                },
              };
            }
            return log;
          });
        });
        dequeuePendingMessage();
      }, 50); // 50msディレイ（UIの段階的表示のため）

      // AI回答完了後は再度スクロールしない（ユーザーメッセージ追加時に既にスクロール済み）
    } catch (error) {
      console.error("Error in handleClick:", error);
      // エラー時はAIメッセージを削除
      setChatLogs((prev) => prev.filter((log) => log.id !== thisAiMessageId));
      dequeuePendingMessage();
    }
  }

  return (
    <div className={styles.question}>
      <div className={styles.container}>
        <div className={styles.form}>
          <div className={styles.fields}>
            <textarea
              className={styles.textarea}
              rows={1}
              placeholder="自由に入力してください"
              value={question}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              maxLength={150}
              style={{ overflowY: 'auto', resize: 'none' }}
            ></textarea>
            <button
              className={styles.button}
              type="button"
              disabled={isStreamingActive}
              onClick={handleClick}
            >
              <IoIosSend className={styles.buttonIcon} fill="white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
